import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, SendIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
export function ContactSection() {
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const response = await fetch('https://formsubmit.co/ajax/kasspi@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          _subject: t('contact.formSubject'),
          _template: 'table'
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        setSubmitError(true);
        setTimeout(() => setSubmitError(false), 5000);
      }
    } catch (error) {
      setSubmitError(true);
      setTimeout(() => setSubmitError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return <section id="kontakt" className="py-24 md:py-32 bg-[#111111]" data-id="element-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-29">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-30">
          <span className="text-[#c8a96e] text-sm font-semibold tracking-widest uppercase mb-4 block" data-id="element-31">
            {t('contact.sectionLabel')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-32">
            {t('contact.title')}
          </h2>
          <p className="text-[#a3a3a3] text-lg max-w-2xl mx-auto" data-id="element-33">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12" data-id="element-34">
          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="space-y-8" data-id="element-35">
            <div className="flex items-start gap-4" data-id="element-36">
              <div className="w-12 h-12 bg-[#c8a96e]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-37">
                <PhoneIcon className="w-5 h-5 text-[#c8a96e]" data-id="element-38" />
              </div>
              <div data-id="element-39">
                <h3 className="text-white font-semibold mb-1" data-id="element-40">
                  {t('contact.phone')}
                </h3>
                <a href="tel:+420603238888" className="text-[#a3a3a3] hover:text-[#c8a96e] transition-colors" data-id="element-41">
                  +420 603 238 888
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-42">
              <div className="w-12 h-12 bg-[#c8a96e]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-43">
                <MailIcon className="w-5 h-5 text-[#c8a96e]" data-id="element-44" />
              </div>
              <div data-id="element-45">
                <h3 className="text-white font-semibold mb-1" data-id="element-46">
                  {t('contact.email')}
                </h3>
                <a href="mailto:info@porschetop.cz" className="text-[#a3a3a3] hover:text-[#c8a96e] transition-colors" data-id="element-47">
                  info@porschetop.cz
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-48">
              <div className="w-12 h-12 bg-[#c8a96e]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-49">
                <MapPinIcon className="w-5 h-5 text-[#c8a96e]" data-id="element-50" />
              </div>
              <div data-id="element-51">
                <h3 className="text-white font-semibold mb-1" data-id="element-52">
                  {t('contact.address')}
                </h3>
                <p className="text-[#a3a3a3]" data-id="element-53">{t('contact.addressValue')}</p>
              </div>
            </div>

            {/* Decorative Image */}
            <div className="mt-8 rounded-xl overflow-hidden" data-id="element-54">
              <img src="https://cdn.magicpatterns.com/uploads/1DWGgAjLTXBtJLJBTJgXHh/IMG_2223.jpg" alt="Porsche detail" className="w-full h-64 object-cover" data-id="element-55" />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} onSubmit={handleSubmit} className="bg-[#1c1c1c] border border-[#2a2a2a] rounded-xl p-8" data-id="element-56">
            <div className="space-y-6" data-id="element-57">
              <div data-id="element-58">
                <label htmlFor="name" className="block text-sm font-medium text-[#a3a3a3] mb-2" data-id="element-59">
                  {t('contact.formName')}
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-[#141414] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white placeholder-[#737373] focus:outline-none focus:border-[#c8a96e] transition-colors" placeholder={t('contact.formNamePlaceholder')} data-id="element-60" />
              </div>

              <div data-id="element-61">
                <label htmlFor="email" className="block text-sm font-medium text-[#a3a3a3] mb-2" data-id="element-62">
                  {t('contact.formEmail')}
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-[#141414] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white placeholder-[#737373] focus:outline-none focus:border-[#c8a96e] transition-colors" placeholder={t('contact.formEmailPlaceholder')} data-id="element-63" />
              </div>

              <div data-id="element-64">
                <label htmlFor="phone" className="block text-sm font-medium text-[#a3a3a3] mb-2" data-id="element-65">
                  {t('contact.formPhone')}
                </label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-[#141414] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white placeholder-[#737373] focus:outline-none focus:border-[#c8a96e] transition-colors" placeholder={t('contact.formPhonePlaceholder')} data-id="element-66" />
              </div>

              <div data-id="element-67">
                <label htmlFor="message" className="block text-sm font-medium text-[#a3a3a3] mb-2" data-id="element-68">
                  {t('contact.formMessage')}
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full bg-[#141414] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white placeholder-[#737373] focus:outline-none focus:border-[#c8a96e] transition-colors resize-none" placeholder={t('contact.formMessagePlaceholder')} data-id="element-69" />
              </div>

              <button type="submit" disabled={isSubmitting || isSubmitted} className={`w-full py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${isSubmitted ? 'bg-green-600 text-white' : submitError ? 'bg-red-600 text-white' : 'bg-[#c8a96e] hover:bg-[#d4b87a] text-[#0a0a0a]'}`} data-id="element-70">
                {isSubmitting ? <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-71">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-72" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-73" />
                    </svg>
                    {t('contact.formSending')}
                  </> : isSubmitted ? <>
                    <CheckIcon className="w-4 h-4" data-id="element-74" />
                    {t('contact.formSent')}
                  </> : submitError ? <>{t('contact.formError')}</> : <>
                    {t('contact.formSubmit')}
                    <SendIcon className="w-4 h-4" data-id="element-75" />
                  </>}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>;
}